import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Search, Loader2, Receipt, Calculator, Package, ScanLine, Camera } from "lucide-react";
import api, { cachedGet } from "@/lib/api";
import { euro, euro0, formatApiErrorDetail } from "@/lib/format";
import { CATEGORIE } from "@/components/spese/categorie";
import { ExpenseGroups } from "@/components/spese/ExpenseGroups";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Link } from "react-router-dom";
import { todayIso } from "@/lib/cantieri";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const EMPTY = { categoria: "Carburante", descrizione: "", fornitore: "", importo: "", data: todayIso(), note: "" };

export default function Spese() {
  const [spese, setSpese] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("tutte");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [openGroups, setOpenGroups] = useState([]);
  const receiptRef = useRef(null);
  const scanRef = useRef(null);

  const [sugg, setSugg] = useState(0);
  const load = () => {
    cachedGet("/expenses").then((r) => setSpese(r.data)).catch(() => {});
    api.get("/operai/spese-suggerite").then((r) => setSugg(r.data.length)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...EMPTY, data: todayIso() }); setEditId(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s, importo: s.importo ?? "", data: (s.data || "").slice(0, 10) }); setEditId(s.id); setOpen(true); };

  const save = async () => {
    if (!form.descrizione && form.categoria === "Altro") { toast.error("Inserisci una descrizione"); return; }
    if (!(Number(form.importo) > 0)) { toast.error("Inserisci un importo valido"); return; }
    setSaving(true);
    try {
      const payload = { ...form, importo: Number(form.importo) };
      if (editId) await api.put(`/expenses/${editId}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editId ? "Spesa aggiornata" : "Spesa aggiunta");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    await api.delete(`/expenses/${toDelete.id}`);
    toast.success("Spesa eliminata");
    setToDelete(null); load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadReceipt = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/ocr/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = r.data || {};
      setForm((prev) => ({
        ...prev,
        importo: d.importo || prev.importo,
        fornitore: d.fonte || prev.fornitore,
        data: d.data || prev.data,
        descrizione: prev.descrizione || d.descrizione || "",
      }));
      toast.success(d.method === "ai" ? "Scontrino letto con l'IA — controlla i dati" : "Scontrino letto (OCR gratuito) — controlla i dati");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setOcrLoading(false); if (receiptRef.current) receiptRef.current.value = ""; }
  };

  // Foto/PDF dello scontrino: l'IA legge i dati e la spesa viene registrata subito.
  const scanReceipt = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/expenses/scan", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 150000 });
      if (r.data.saved) {
        const x = r.data.expense;
        toast.success(`Spesa registrata: ${x.categoria} · ${euro(x.importo)}`, {
          description: [x.fornitore, x.descrizione].filter(Boolean).join(" · ") || undefined,
          action: { label: "Modifica", onClick: () => openEdit(x) },
        });
        setOpenGroups((g) => (g.includes(x.categoria) ? g : [...g, x.categoria]));
        load();
      } else {
        const d = r.data.dati || {};
        setForm({ ...EMPTY, categoria: d.categoria || "Altro", fornitore: d.fonte || "", data: d.data || todayIso(), descrizione: d.descrizione || "" });
        setEditId(null); setOpen(true);
        toast.info("Non ho trovato l'importo: completalo tu e salva");
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Lettura non riuscita"); }
    finally { setScanning(false); }
  };

  const filtered = spese.filter((s) => {
    const mq = !q || [s.descrizione, s.fornitore, s.categoria].join(" ").toLowerCase().includes(q.toLowerCase());
    const mc = catFilter === "tutte" || s.categoria === catFilter;
    return mq && mc;
  });
  const totale = filtered.reduce((sum, s) => sum + Number(s.importo || 0), 0);
  const totaleAnno = spese.filter((s) => (s.data || "").slice(0, 4) === String(new Date().getFullYear())).reduce((sum, s) => sum + Number(s.importo || 0), 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="rounded-xl border border-border bg-card p-3 sm:p-5 min-w-0" data-testid="spese-kpi-totale">
          <div className="hidden sm:flex w-11 h-11 rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400 items-center justify-center"><Receipt className="w-5 h-5" /></div>
          <div className="sm:mt-4 font-heading text-base sm:text-3xl font-extrabold truncate">{euro0(totale)}</div>
          <div className="text-[11px] sm:text-sm font-medium mt-0.5 leading-tight">Totale spese {catFilter !== "tutte" ? `· ${catFilter}` : "(filtro)"}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3 sm:p-5 min-w-0" data-testid="spese-kpi-anno">
          <div className="hidden sm:flex w-11 h-11 rounded-lg bg-primary/10 text-primary items-center justify-center"><Calculator className="w-5 h-5" /></div>
          <div className="sm:mt-4 font-heading text-base sm:text-3xl font-extrabold truncate">{euro0(totaleAnno)}</div>
          <div className="text-[11px] sm:text-sm font-medium mt-0.5 leading-tight">Spese anno {new Date().getFullYear()}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-3 sm:p-5 min-w-0" data-testid="spese-kpi-movimenti">
          <div className="hidden sm:flex w-11 h-11 rounded-lg bg-secondary text-foreground items-center justify-center"><Package className="w-5 h-5" /></div>
          <div className="sm:mt-4 font-heading text-base sm:text-3xl font-extrabold">{spese.length}</div>
          <div className="text-[11px] sm:text-sm font-medium mt-0.5 leading-tight">Movimenti registrati</div>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-secondary/40 px-4 py-2.5 text-sm text-muted-foreground" data-testid="spese-info">
        Le spese di gestione (benzina, assicurazioni, commercialista…) vengono sottratte dall'<span className="font-semibold text-foreground">utile netto</span> in Panoramica.
      </div>

      {sugg > 0 && (
        <Link to="/cantieri?tab=operai&spese=1" className="block rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-4 py-3 text-sm hover:bg-amber-100 transition-colors" data-testid="spese-operai-banner">
          <b>{sugg}</b> {sugg === 1 ? "spesa sembra" : "spese sembrano"} di un operaio: <span className="text-primary font-semibold underline">spostale sulla sua scheda</span> così non vengono contate due volte.
        </Link>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Cerca spesa…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="spese-search-input" />
        </div>
        <div className="flex items-center gap-2 flex-wrap sm:ml-auto">
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="w-48" data-testid="spese-cat-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutte">Tutte le categorie</SelectItem>
              {CATEGORIE.map((c) => <SelectItem key={c.key} value={c.key}>{c.key}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={openNew} data-testid="expense-add-button"><Plus className="w-4 h-4 mr-1.5" /> Nuova Spesa</Button>
          <input ref={scanRef} type="file" accept="image/*,.pdf" onChange={scanReceipt} className="hidden" data-testid="expense-scan-input" />
          <Button onClick={() => scanRef.current?.click()} disabled={scanning} data-testid="expense-scan-button">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Camera className="w-4 h-4 mr-1.5" />} {scanning ? "Leggo lo scontrino…" : "Foto / carica scontrino"}
          </Button>
        </div>
      </div>

      <ExpenseGroups spese={filtered} open={q ? CATEGORIE.map((c) => c.key) : openGroups} onOpenChange={setOpenGroups} onEdit={openEdit} onDelete={setToDelete} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Modifica spesa" : "Nuova spesa di gestione"}</DialogTitle>
            <DialogDescription className="sr-only">Inserisci una spesa di gestione (categoria, importo, data).</DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <input ref={receiptRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={uploadReceipt} className="hidden" data-testid="expense-receipt-input" />
              <Button type="button" variant="secondary" className="w-full" onClick={() => receiptRef.current?.click()} disabled={ocrLoading} data-testid="expense-receipt-upload">
                {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ScanLine className="w-4 h-4 mr-2" />} Carica scontrino/bolla (lettura automatica)
              </Button>
              <p className="text-xs text-muted-foreground mt-1.5">Scatta una foto o carica un PDF: compilo in automatico importo, fonte e data (poi modificabili).</p>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Categoria</Label>
              <Select value={form.categoria} onValueChange={(c) => setForm((f) => ({ ...f, categoria: c }))}>
                <SelectTrigger data-testid="expense-categoria-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIE.map((c) => <SelectItem key={c.key} value={c.key}>{c.key}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2"><Label>Descrizione</Label><Input value={form.descrizione} onChange={set("descrizione")} placeholder="Es. Rifornimento furgone" data-testid="expense-descrizione-input" /></div>
            <div className="space-y-2"><Label>Fornitore</Label><Input value={form.fornitore} onChange={set("fornitore")} placeholder="Es. Eni, Generali…" /></div>
            <div className="space-y-2"><Label>Importo € *</Label><Input type="number" min="0" step="0.01" value={form.importo} onChange={set("importo")} data-testid="expense-importo-input" /></div>
            <div className="space-y-2"><Label>Data</Label><DateInput value={form.data} onChange={set("data")} data-testid="expense-data-input" /></div>
            <div className="space-y-2 sm:col-span-2"><Label>Note</Label><Textarea value={form.note} onChange={set("note")} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="expense-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la spesa?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.descrizione || toDelete?.categoria} · {euro(toDelete?.importo)} sarà rimossa.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-expense">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
